import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Loader from '../../../common/Loader';
import scogo_apis from '../../../scogo_apis';
import { openMediaViewModal } from '../../../actions/modal';
import { clickableTextClass } from '../../../common/Css';
import { getMediaViewAttachments } from '../../../features/pav/pavAssetUtils';
import { getSavedAssetVerification } from '../../../features/pav/PavAssetReviewCard';

// This project overrides Tailwind's height scale, so thumbnails are pinned with
// explicit values to stay square.
const TILE_CLASS = 'h-[7rem] w-[7rem]';

const VERIFICATION_STATUS = {
    verified: { label: 'Verified', icon: 'check_circle', className: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
    rejected: { label: 'Rejected', icon: 'cancel', className: 'border-red-300 bg-red-50 text-red-700' },
    pending: { label: 'Pending', icon: 'schedule', className: 'border-amber-300 bg-amber-50 text-amber-800' },
};

const getVerificationStatus = (asset) => {
    const verification = getSavedAssetVerification(asset);
    if (verification === true) return VERIFICATION_STATUS.verified;
    if (verification === false) return VERIFICATION_STATUS.rejected;
    return VERIFICATION_STATUS.pending;
};

const formatDateTime = (value) => (value && moment(value).isValid() ? moment(value).format('DD MMM YYYY, hh:mm A') : '');

// An asset that has not been through PAV yet still comes back with
// `{ attachments: [] }`, so only a ticket, a decision or a photo counts as data.
const hasPavData = (pav) => Boolean(pav?.ticket_id || pav?.attachments?.length > 0 || (pav?.is_verified ?? null) !== null);

const StatusMessage = ({ icon, children }) => (
    <div role='status' className='flex items-center justify-center gap-2 rounded-lg border border-dashed border-scogoddd bg-white px-4 py-6 text-font11 font-normal text-slate-600'>
        {icon}
        {children}
    </div>
);

const Section = ({ icon, title, badge, children }) => (
    <section className='overflow-hidden rounded-xl border border-scogoddd bg-white shadow-sm'>
        <div className='flex items-center gap-2 border-b border-scogoddd bg-scogof8 px-3 py-2'>
            <span className='material-icons-outlined text-font14 text-scogoprimary'>{icon}</span>
            <p className='min-w-0 flex-1 truncate text-font12 font-semibold text-scogo21'>{title}</p>
            {badge}
        </div>
        <div className='p-3'>{children}</div>
    </section>
);

const DetailItem = ({ label, children }) => (
    <div className='min-w-0'>
        <p className='truncate text-font09 font-semibold uppercase tracking-wide text-slate-500'>{label}</p>
        <div className='mt-0.5 break-words text-font11 font-medium text-scogo21'>{children || '-'}</div>
    </div>
);

const AttachmentTile = ({ attachment, index, onOpen }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const isImage = String(attachment.mime_type || '').startsWith('image/');

    return (
        <button
            type='button'
            onClick={() => onOpen(index)}
            title={attachment.name}
            className={`relative shrink-0 overflow-hidden rounded-md border bg-white shadow-sm transition-transform hover:scale-105 ${attachment.is_rejected ? 'border-red-300' : 'border-scogoddd'} ${TILE_CLASS}`}
        >
            {isImage && !imageFailed ? (
                <img
                    src={attachment.thumbnail_url || attachment.url}
                    alt={attachment.name}
                    loading='lazy'
                    decoding='async'
                    className='h-full w-full object-cover'
                    onError={() => setImageFailed(true)}
                />
            ) : (
                <span className='flex h-full w-full items-center justify-center bg-blue-50 text-blue-700'>
                    <span className='material-icons text-font18'>insert_drive_file</span>
                </span>
            )}
            {attachment.is_rejected && (
                <span className='absolute right-1 top-1 rounded-full bg-red-600 px-1.5 py-0.5 text-font09 font-semibold text-white'>Rejected</span>
            )}
            <span className='absolute inset-x-0 bottom-0 truncate bg-slate-900/70 px-1 py-0.5 text-font09 font-medium text-white'>{attachment.name}</span>
        </button>
    );
};

// Read-only view of an asset's physical verification (PAV) data: the decision,
// the asset's last ticket and the photos captured during verification.
export default function ViewPavData({ assetId, ticket }) {
    const dispatch = useDispatch();
    const [asset, setAsset] = useState(null);
    const [isLoading, setIsLoading] = useState(Boolean(assetId));
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const getAsset = async () => {
            setIsLoading(true);
            try {
                const response = await scogo_apis.inventory.getAssetById({}, { id: assetId });
                setAsset(response?.data);
            } catch (err) {
                // Server errors can expose routes and IDs, so users only see a generic message.
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        };

        if (assetId) getAsset();
    }, [assetId]);

    const pav = asset?.pav_verification_data;
    const attachments = useMemo(() => getMediaViewAttachments(pav?.attachments || []), [pav]);

    const previewAttachment = useCallback((index) => {
        dispatch(openMediaViewModal({ attachments, openedMediaIndex: index, showSenderDetail: false }));
    }, [attachments, dispatch]);

    if (isLoading) {
        return (
            <div className='bg-scogof8 p-3'>
                <StatusMessage icon={<Loader size='16' thickness='2' color='#12355F' />}>Loading PAV data...</StatusMessage>
            </div>
        );
    }

    if (hasError || !hasPavData(pav)) {
        return (
            <div className='bg-scogof8 p-3'>
                <StatusMessage icon={<span className='material-icons-outlined text-font16 text-scogo99'>{hasError ? 'info' : 'schedule'}</span>}>
                    {hasError ? 'Unable to load PAV data. Please try again later.' : 'PAV is pending for this asset.'}
                </StatusMessage>
            </div>
        );
    }

    const status = getVerificationStatus(asset);
    // Owner and location are optional on an asset, so they are listed only when set.
    const optionalDetails = [
        ['Owner Name', asset.owner?.name],
        ['Owner Email', asset.owner?.email],
        ['Location Name', asset.location?.name || asset.location?.external_id],
    ].filter(([, value]) => String(value ?? '').trim() !== '');

    return (
        <div className='max-h-[80vh] space-y-3 overflow-y-auto bg-scogof8 p-3'>
            <Section
                icon='fact_check'
                title='Verification'
                badge={(
                    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-font10 font-semibold ${status.className}`}>
                        <span className='material-icons text-font12'>{status.icon}</span>
                        {status.label}
                    </span>
                )}
            >
                <div className='grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-3'>
                    {ticket?.ticket_number && (
                        <DetailItem label='Last Ticket'>
                            <a href={`/chats/${ticket.id}?tab=alltickets`} target='_blank' rel='noreferrer' className={clickableTextClass}>
                                {ticket.ticket_number}
                            </a>
                        </DetailItem>
                    )}
                    {optionalDetails.map(([label, value]) => <DetailItem key={label} label={label}>{value}</DetailItem>)}
                    <DetailItem label='Verified At'>{formatDateTime(pav.verified_at)}</DetailItem>
                </div>
            </Section>

            <Section
                icon='photo_library'
                title='Attachments'
                badge={<span className='rounded-full bg-slate-100 px-2 py-0.5 text-font09 font-semibold text-slate-600'>{attachments.length}</span>}
            >
                {attachments.length > 0 ? (
                    <div className='flex flex-wrap gap-2'>
                        {attachments.map((attachment, index) => (
                            <AttachmentTile key={attachment.id} attachment={attachment} index={index} onOpen={previewAttachment} />
                        ))}
                    </div>
                ) : (
                    <p className='rounded-md border border-dashed border-scogoddd bg-scogof8 px-3 py-2 text-center text-font10 font-normal text-slate-600'>No attachments were uploaded for this asset</p>
                )}
            </Section>
        </div>
    );
}
